import type { NavigationGuard, NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useEmployeeStore } from '@/stores/employee'
import { useUserStore } from '@/stores/user'
import {
  HOMEPAGE_PAGE_NAME,
  TEAM_MANAGEMENT_PAGE_NAME,
  BOOKINGS_PAGE_NAME,
  COMPANY_SETTINGS_PAGE_NAME,
  LOYALTY_PAGE_NAME,
  REPORTING_PAGE_NAME
} from '@/constants/gtm-page-names'
import { AAB_PERMISSIONS } from '@/constants/aab-permissions'

const requireAabPermissions = (
  permissions: AAB_PERMISSIONS | AAB_PERMISSIONS[],
  redirectRouteName = 'dashboard'
): NavigationGuard => {
  const requiredPermissions = Array.isArray(permissions) ? permissions : [permissions]

  return async (
    _to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const userStore = useUserStore()
    const employeeStore = useEmployeeStore()

    try {
      const isUserEmpty = Object.keys(userStore.user).length === 0

      if (isUserEmpty) {
        await userStore.refreshUser()
      }

      if (!userStore.isAab) {
        return next({ name: 'dashboard' })
      }

      if (!employeeStore.employee) {
        await employeeStore.setEmployee()
      }

      return employeeStore.hasAnyPermission(requiredPermissions)
        ? next()
        : next({ name: redirectRouteName })
    } catch {
      return next({ name: redirectRouteName })
    }
  }
}

export const aabRoutes = [
  {
    name: 'aab',
    path: 'aab',
    beforeEnter: requireAabPermissions(AAB_PERMISSIONS.AAB_COMPANY_PORTAL_READ),
    children: [
      {
        path: 'dashboard',
        name: 'aab-dashboard',
        meta: {
          gtmId: 'GTM_AAB_PAGE_DASHBOARD',
          pageName: HOMEPAGE_PAGE_NAME
        },
        component: () => import('../views/aab/DashboardAab.vue')
      },
      {
        path: 'collaborators-management',
        name: 'aab-collaborators-management',
        meta: {
          gtmId: 'GTM_AAB_PAGE_TEAM_MANAGEMENT',
          pageName: TEAM_MANAGEMENT_PAGE_NAME
        },
        component: () => import('../views/aab/CollaboratorsManagementAab.vue')
      },
      {
        path: 'bookings-management',
        name: 'aab-bookings-management',
        meta: {
          gtmId: 'GTM_AAB_PAGE_BOOKINGS',
          pageName: BOOKINGS_PAGE_NAME
        },
        component: () => import('../views/aab/BookingsManagementAab.vue')
      },
      {
        path: 'company-information',
        name: 'aab-company-information',
        meta: {
          gtmId: 'GTM_AAB_PAGE_COMPANY_SETTINGS',
          pageName: COMPANY_SETTINGS_PAGE_NAME
        },
        component: () => import('../views/aab/CompanyInformationAab.vue')
      },
      {
        path: 'loyalty-management',
        name: 'aab-loyalty-management',
        meta: {
          gtmId: 'GTM_AAB_PAGE_LOYALTY',
          pageName: LOYALTY_PAGE_NAME
        },
        component: () => import('../views/aab/LoyaltyManagementAab.vue')
      },
      {
        path: 'reporting',
        name: 'aab-reporting',
        meta: {
          gtmId: 'GTM_AAB_PAGE_REPORTING',
          pageName: REPORTING_PAGE_NAME
        },
        component: () => import('../views/aab/ReportingAab.vue')
      }
    ]
  }
]
